import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "@mui/material";
import { ReactComponent as exclamatory } from "@/assets/icons/exclamatory.svg";
import { Inspection } from "@/types/Inspection.type";
import { Preview } from "@/features/inspection-details/components/preview/Preview";
import styles from "./ExistingOpenInspectionDialog.module.scss";

export interface ExistingOpenInspectionDialogProps {
    openInspection: Inspection;
    onResume: () => void;
    onDiscardConfirm: () => void;
    /** Called when user cancels/closes the dialog without choosing an action (optional). */
    onCancelDiscard?: () => void;
}

export const ExistingOpenInspectionDialog: FC<ExistingOpenInspectionDialogProps> = ({
    openInspection,
    onResume,
    onDiscardConfirm,
    onCancelDiscard,
}) => {
    const { t } = useTranslation();
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);

    const handleDiscardClick = () => setShowDiscardConfirmation(true);
    const handleConfirmDiscard = () => onDiscardConfirm();
    const handleCancelDiscardConfirmation = () => setShowDiscardConfirmation(false);

    const footer = (
        <div className={styles.actions}>
            <button
                type="button"
                className={`${styles.actionButton} ${styles.danger}`}
                onClick={handleDiscardClick}
            >
                {t("discard_and_start", "Discard & start this inspection")}
            </button>
            <button
                type="button"
                className={`${styles.actionButton} ${styles.primary}`}
                onClick={onResume}
            >
                {t("resume_existing", "Resume existing inspection")}
            </button>
        </div>
    );

    return (
        <>
            <Preview
                inspection={openInspection}
                onClose={onCancelDiscard ?? (() => undefined)}
                showCloseIcon={false}
                title={t("open_inspection_exists_title", "You have an inspection already open")}
                subtitle={t(
                    "open_inspection_exists_message",
                    "Would you like to resume the existing inspection or discard it and start this one?"
                )}
                headerIcon={
                    <SvgIcon
                        component={exclamatory}
                        viewBox="0 0 40 40"
                        className={styles.warningIcon}
                    />
                }
                footer={footer}
            />
            {showDiscardConfirmation && (
                <div className={styles.confirmOverlay}>
                    <div className={styles.confirmModal}>
                        <h2 className={styles.confirmTitle}>
                            {t("discard_open_inspection_confirm_title", "Are you sure?")}
                        </h2>
                        <p className={styles.confirmMessage}>
                            {t(
                                "discard_open_inspection_confirm_message",
                                "Are you sure you want to delete the open inspection? If you proceed, you will lose all the data you recorded during the inspection."
                            )}
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.confirmCancel}`}
                                onClick={handleCancelDiscardConfirmation}
                            >
                                {t("Cancel")}
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionButton} ${styles.danger}`}
                                onClick={handleConfirmDiscard}
                            >
                                {t("discard_confirm_button", "Yes, delete")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
